/**
 * Reason codes returned by the C1 routes, grouped by concern (C1 spec §5.4).
 * C1 adds only codes that a route declares. C2 (Mahdi) owns their FR/EN texts, adds the codes
 * no route returns, and may rename them in a contract PR.
 */

/** Session and initData failures (master X1, technical spec §4). */
export const AUTH_REASON_CODES = [
  'UNAUTHENTICATED',
  'TOKEN_EXPIRED',
  'INIT_DATA_INVALID',
  'INIT_DATA_EXPIRED',
] as const;

/** Request-level failures: idempotency (master X3) and rate limits (standards §8). */
export const REQUEST_REASON_CODES = [
  'IDEMPOTENCY_KEY_REQUIRED',
  'IDEMPOTENCY_KEY_REUSED',
  'RATE_LIMITED',
  'IDEMPOTENCY_KEY_IN_PROGRESS', // moved from C2, standard on keyed writes (R9, proposal)
  'SERVICE_UNAVAILABLE', // moved from C2, standard 503 of bearer routes (R9, Decision 2)
] as const;

/** Telegram identification states of FXM-01 §5 (technical spec §6). */
export const TELEGRAM_LOGIN_REASON_CODES = [
  'TELEGRAM_PHONE_NUMBER_INVALID', // provisional until Q70
  'TELEGRAM_CODE_INVALID',
  'TELEGRAM_CODE_EXPIRED',
  'TELEGRAM_CODE_RATE_LIMITED', // Q33
  'TELEGRAM_PASSWORD_INVALID',
  'TELEGRAM_LOGIN_NOT_STARTED',
  'TELEGRAM_SESSION_LOST',
  'TELEGRAM_ACCOUNT_MISMATCH', // moved from C2 (Decision 2)
  'TELEGRAM_UNAVAILABLE', // moved from C2 (Decision 2)
] as const;

/** Trading account and consent states of FXM-01 §5, §6 cases 5 to 7 and 11. */
export const TRADING_ACCOUNT_REASON_CODES = [
  'TRADING_ACCOUNT_CREDENTIALS_REJECTED',
  'TRADING_ACCOUNT_SERVER_NOT_FOUND',
  'TRADING_ACCOUNT_READ_ONLY',
  'TRADING_ACCOUNT_LINKED_TO_OTHER_TRADER',
  'TRADING_ACCOUNT_NETTING_NOT_SUPPORTED', // P1, provisional until Q264
  'TRADING_ACCOUNT_BALANCE_LOW',
  'TRADING_ACCOUNT_UNREACHABLE',
  'TRADING_ACCOUNT_ALREADY_CONNECTED',
  'TRADING_ACCOUNT_NOT_CONNECTED',
  'CONSENT_VERSION_UNKNOWN', // with Q17, Q72
] as const;

/** Channel and subscription states (FXM-01 §5, FXM-02 §6 case 11). */
export const CHANNEL_REASON_CODES = [
  'CHANNEL_INACCESSIBLE',
  'SUBSCRIPTION_ALREADY_EXISTS',
  'CONFIRMATION_UNANSWERED',
  'CHANNEL_NOT_CONFIGURED', // moved from C2 (B-D6)
] as const;

/** Position states shown in E8 and E10 (FXM-04 §6 case 11, FXM-02 §4). */
export const POSITION_REASON_CODES = [
  'STOP_LOSS_REFUSED_BY_BROKER',
  'POSITION_CLOSE_FAILED',
] as const;

/** Settings routes (FXM-02 §4–§6): moved from C2 with the same values (Decision 5, B-D16). */
export const SETTINGS_ROUTE_REASON_CODES = [
  'SETTINGS_FALLBACK_SL_REQUIRED',
  'SETTINGS_VERSION_CONFLICT',
  'RISK_PREVIEW_UNAVAILABLE',
] as const;

/** All C1 groups, keyed by concern (used by tests and the README). */
export const CONTRACT_REASON_CODE_GROUPS = {
  auth: AUTH_REASON_CODES,
  request: REQUEST_REASON_CODES,
  telegramLogin: TELEGRAM_LOGIN_REASON_CODES,
  tradingAccount: TRADING_ACCOUNT_REASON_CODES,
  channels: CHANNEL_REASON_CODES,
  positions: POSITION_REASON_CODES,
  settings: SETTINGS_ROUTE_REASON_CODES,
} as const;
